<script>
    import {fallbackLocale, languages} from './i18n';
    import {_, getLocaleFromNavigator, isLoading, locale} from 'svelte-i18n';
    import { currentPath, navigate, matchPath } from './lib/router/router.js';
    import Login from './Login.svelte';
    import Shop from './Shop.svelte';
    import Home from './Home.svelte';
    import BuyPack from './BuyPack.svelte';
    import GdprBanner from './lib/GdprBanner.svelte';
    import {user, isRedirected, adventCalendarEvent, storeSection} from './stores';
    import {tagNetworkFailure, tagBodyReadFailure} from './lib/network-failure.js';
    import {adventVariant} from './lib/advent-variant.js';
    import Fa from 'svelte-fa';
    import {faDiscord, faFacebook, faInstagram, faTiktok} from '@fortawesome/free-brands-svg-icons';
    import 'animate.css';
    import {login_route, logout_route} from "./constants.js";
    import {logMetric} from "./metrics.js";
    import {faBookOpenCover} from "@fortawesome/pro-duotone-svg-icons/faBookOpenCover";
    import Tos from "./Tos.svelte";
    import Privacy from "./Privacy.svelte";
    import DeleteAccount from "./DeleteAccount.svelte";
    import RfbMigration from "./RfbMigration.svelte";
    import {isMigrationPagePath} from "./lib/rfb-migration.js";
    import {getCurrentTheme, loadThemeConfig, themeHasComponent} from './themeLoader.js';
    import {onMount} from 'svelte';
    // Static imports for theme components (avoids dynamic import hash issues)
    import WinterSnowfall from './themes/winter_theme/Snowfall.svelte';

    // Theme support
    let currentTheme = getCurrentTheme();
    let themeConfig = null;
    let SnowfallComponent = null;
    let socialIconColor = 'gold'; // default color
    let themeClass = '';

    onMount(async () => {
        if (currentTheme) {
            themeConfig = await loadThemeConfig(currentTheme);
            if (themeConfig) {
                socialIconColor = themeConfig.socialIconColor || 'gold';
                themeClass = themeConfig.themeClass || '';

                // Apply theme class to body element
                if (themeClass) {
                    document.body.classList.add(themeClass);
                }

                // Load theme stylesheet (only if not already injected at build time)
                if (themeConfig.hasStylesheet && !document.head.querySelector(`link[href*="/themes/${currentTheme}/theme.css"]`)) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = '/themes/' + currentTheme + '/theme.css?v=' + version;
                    document.head.appendChild(link);
                }

                // Load Snowfall component using static import (prevents chunk hash mismatches)
                if (themeHasComponent(themeConfig, 'snowfall')) {
                    if (currentTheme === 'winter_theme') {
                        SnowfallComponent = WinterSnowfall;
                    }
                }
            }
        }
    });

    window['ga-disable-G-KQKL2ZNVJY'] = true;

    function initAnalytics() {
        window['ga-disable-G-KQKL2ZNVJY'] = false;
    }

    function initMarketing() {
        gtag('consent', 'update', {
            ad_storage: 'granted',
        });
    }

    function initTracking() {
        gtag('consent', 'update', {
            analytics_storage: 'granted',
        });
    }

    const currentYear = new Date().getFullYear();
    const version = "__VERSION__";

    let initialLocale;
    const detectedLocale =
        localStorage.getItem('locale') || getLocaleFromNavigator();
    if (languages.indexOf(detectedLocale) > -1) initialLocale = detectedLocale;
    if (!initialLocale && detectedLocale.indexOf('-') > 0) {
        const foundLng = languages.find(
            (l) => detectedLocale.indexOf(l + '-') === 0,
        );
        if (foundLng) initialLocale = foundLng;
    }
    if (!initialLocale) initialLocale = fallbackLocale;

    locale.set(initialLocale);

    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');
    const autocompleteUsername = urlParams.get('auto');

    let uValue, tValue, pValue;
    let packTitle;
    
    let loginResponse = {
        error: null,
        user: null,
    };

    const silentLogout = (u, t, packId) => {
        window.history.replaceState({}, document.title, window.location.pathname);

        fetch(logout_route, {
            method: 'GET',
        })
            .catch(tagNetworkFailure)
            .then((response) => {
                if (!response.ok) throw new Error(`Logout failed: ${response.status}`);
                localStorage.removeItem('user');
                localStorage.removeItem('storeSection');
                user.set(null);
            })
            .catch((error) => {
                if (error?.isNetworkFailure) {
                    // Tagged at the fetch boundary, so this is a dropped connection on the
                    // way out rather than an app fault (Rollbar #587). silentLogin() runs
                    // from .finally() either way, so the user is not left stuck — nothing
                    // actionable. Deliberately not inferred from `instanceof TypeError`:
                    // the .then() above touches response.ok and localStorage, so a genuine
                    // bug there throws a TypeError that must stay at error level.
                    Rollbar.info("Silent logout network failure: " + error);
                } else {
                    Rollbar.error("Silent logout error:" + error);
                }
            })
            .finally(() => {
                silentLogin(u, t, packId);
            });
    };

    const silentLogin = (u, t, packId) => {
        let username = u;
        let password = t;

        let loginFields = {
            username,
            password,
        };

        loginResponse = {
            error: null,
            user: null,
        };
        fetch(login_route, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(loginFields),
        })
            .catch(tagNetworkFailure)
            .then((response) => {
                if (!response.ok) {
                    const err = new Error(`Login failed: ${response.status}`);
                    err.status = response.status;
                    throw err;
                }
                return response.json().catch(tagBodyReadFailure);
            })
            .then((data) => {
                if (data.statusCode) {
                    loginResponse = {
                        ...loginResponse,
                        error: data.message,
                    };
                    isRedirected.set(false);
                } else {
                    loginResponse = {
                        ...loginResponse,
                        user: data.user,
                    };
                    localStorage.removeItem('user');
                    localStorage.removeItem('player_name');
                    loginResponse.user.username = u;
                    user.set(loginResponse);
                    localStorage.setItem('player_name', u);
                    localStorage.setItem('user', JSON.stringify(loginResponse));
                    isRedirected.set(true);

                    if(packId && packId !== 'undefined') {
                        const packTitleParam = packTitle ? `&packTitle=${encodeURIComponent(packTitle)}` : '';
                        window.location.href = `/buyPack?packId=${packId}${packTitleParam}`;
                    }
                }
            })
            .catch((error) => {
                if (error.status && error.status >= 400 && error.status < 500) {
                    Rollbar.info("Silent login auth failure: " + error);
                } else if (error?.isNetworkFailure) {
                    // Tagged at the fetch boundary or on the body read, so this really is a
                    // dropped connection mid-login — before the status line (Rollbar #491) or
                    // while the body was still streaming after a 201 (Rollbar #439) — and not
                    // actionable. Deliberately not inferred from a missing .status: the handlers
                    // above touch loginResponse.user.username and data.statusCode, so a genuine
                    // bug there throws a status-less TypeError that must stay critical.
                    Rollbar.info("Silent login network failure: " + error);
                } else {
                    Rollbar.critical("Silent login error: " + error);
                }
            })
            .finally(() => {
                logMetric({
                    login: {
                        username: u,
                        source: 'silent',
                        params: redirect,
                        ...(loginResponse.error !== null && {error: loginResponse.error})
                    },
                });

                window.history.replaceState({}, document.title, window.location.pathname);
            });
    }

    if (redirect) {
        window.history.replaceState({}, document.title, window.location.pathname);
        console.log(redirect);
        let decoded = atob(redirect);
        const keyValuePairs = decoded.split("&");

        for (let i = 0; i < keyValuePairs.length; i++) {
            const [key, value] = keyValuePairs[i].split("=");
            if (key === "u") {
                uValue = value;
            }
            if (key === "t") {
                tValue = value;
            }
            
            if(key === "packId") {
                pValue = value;
            }
            
            if(key === "packTitle") {
                packTitle = value;
            }
        }

        if (uValue && tValue) {
            silentLogout(uValue, tValue, pValue);
        }
    }

    if (autocompleteUsername) {
        window.history.replaceState({}, document.title, window.location.pathname);

        let decoded = atob(autocompleteUsername);
        const keyValuePairs = decoded.split("&");

        for (let i = 0; i < keyValuePairs.length; i++) {
            const [key, value] = keyValuePairs[i].split("=");
            if (key === "u") {
                uValue = value;
            }
        }

        if (uValue) {
            localStorage.setItem('player_name', uValue);
        }
    }

    // A function to encode the email body
    function encodeMailtoBody(username, userId) {
        return encodeURIComponent(`\n\nUsername: ${username}\nID: ${userId}`);
    }

</script>

<svelte:head>
    <script>
        var _rollbarConfig = {
            accessToken: "0a738cd169304024810fcf1ed0e2b225",
            captureUncaught: true,
            captureUnhandledRejections: true,
            captureIp: 'anonymize',
            checkIgnore: function(isUncaught, args, payload) {
                var trace = payload && payload.body && payload.body.trace;
                var frames = trace && trace.frames;
                if (frames) {
                    for (var i = 0; i < frames.length; i++) {
                        var filename = frames[i].filename;
                        // iabjs:// is Facebook's Android in-app-browser injected script scheme
                        // (navigation performance logger etc.) — Rollbar #2531
                        if (filename && (filename.indexOf('chrome-extension://') === 0 || filename.indexOf('moz-extension://') === 0 || filename.indexOf('webkit-masked-url://') === 0 || filename.indexOf('iabjs://') === 0)) {
                            return true;
                        }
                    }
                }
                // Filter errors from third-party injected scripts (Facebook in-app browser, etc.)
                var message = trace && trace.exception && trace.exception.message;
                if (message && message.indexOf('webkit.messageHandlers') !== -1) {
                    return true;
                }
                // Filter WKWebView native bridge timeouts (DuckDuckGo browser, other iOS WKWebView wrappers)
                // when the host app is suspended/backgrounded by iOS — non-actionable, third-party host issue
                if (message && message.indexOf('WKWebView API client did not respond to this postMessage') !== -1) {
                    return true;
                }
                // Filter Svelte reactivity stack overflow on redirect race conditions (non-actionable, user recovers automatically)
                if (message && message.indexOf('Maximum call stack size exceeded') !== -1) {
                    return true;
                }
                // Filter third-party probe noise (antivirus / link-scanner injected scripts using a COM-style bridge):
                // "Object Not Found Matching Id:N, MethodName:update, ParamCount:N" — no app stack frame, non-actionable
                if (message && message.indexOf('Object Not Found Matching Id') !== -1) {
                    return true;
                }
                // Filter Firebase SDK IndexedDB failures on WebKit privacy browsers (Safari private mode, DuckDuckGo, etc.)
                // which abort/auto-commit IDB transactions — non-actionable, Firebase degrades gracefully.
                // "The database connection is closing" is the same family from the other side: the user
                // cleared site data mid-session and Firebase's write raced the wipe (Rollbar #2527).
                // "Database deleted by request of the user" is that same wipe surfacing through
                // Firebase's IDB open instead — an unhandled rejection with frames: [] (Rollbar #28).
                // "An internal error was encountered in the Indexed Database server" is WebKit's IDB
                // backend itself failing under the same SDK on iPhone / iPad, frames: [] (Rollbar #139).
                // "Connection is closing because of: ..." is Chrome force-closing that connection, for
                // a site-data wipe ("Force close delete origin", #2573) or a full disk
                // (FILE_ERROR_NO_SPACE, #2576), frames: [].
                if (message && (message.indexOf('Attempt to delete range from database without an in-progress transaction') !== -1
                    || message.indexOf('without an in-progress transaction') !== -1
                    || message.indexOf('The database connection is closing') !== -1
                    || message.indexOf('Database deleted by request of the user') !== -1
                    || message.indexOf('An internal error was encountered in the Indexed Database server') !== -1
                    || message.indexOf('Connection is closing because of:') !== -1)) {
                    return true;
                }
                // Filter content-script noise injected by Firefox/Brave on iOS (both WebKit, Brave iOS
                // is built on Firefox iOS): their user scripts reference window.__firefox__ and throw in
                // the page's global scope ("global code@/:1") before it's defined. Not our code.
                if (message && message.indexOf('__firefox__') !== -1) {
                    return true;
                }
                // Filter crypto-wallet provider injection noise (Brave Wallet, MetaMask, etc.): their
                // injected scripts touch window.ethereum in the page's global scope ("global code@/:1")
                // before it exists and throw. The app has no crypto features, so any 'ethereum' error
                // here is third-party injection, non-actionable.
                if (message && message.indexOf('ethereum') !== -1) {
                    return true;
                }
                // Filter Dark Reader extension injection noise (Rollbar #2218): its injected script
                // references its own DarkReader global before it exists and throws at the page URL,
                // line 1. The app never touches that global.
                if (message && message.indexOf('DarkReader') !== -1) {
                    return true;
                }
                // Filter old-Safari (iOS 12/13, Safari <14) parse failures of modern JS in
                // third-party scripts such as Cloudflare's beacon.min.js. This exact wording is
                // Safari's when it can't parse newer syntax — it never comes from our own
                // transpiled bundle, and the browser is simply too old to support (non-actionable).
                if (message && message.indexOf("Expected an opening '(' before a method's parameter list") !== -1) {
                    return true;
                }
                // Filter browser-extension messaging failures (Rollbar #1577). runtime.sendMessage
                // is the WebExtension API — page scripts cannot call it, so this is always an
                // extension talking to a tab that has already gone away. These arrive without a
                // chrome-extension:// frame, so the origin check above does not catch them.
                if (message && message.indexOf('runtime.sendMessage') !== -1) {
                    return true;
                }
                // Filter errors thrown entirely inside third-party analytics/beacon scripts.
                // Cloudflare's beacon calls Array.prototype.at(), which is missing on the
                // Chromium 88 webview that Facebook's in-app browser still ships, so it throws
                // "t.entries.at is not a function" / "this.i.at is not a function" without our
                // bundle ever being on the stack (Rollbar #2437 / #2438). Matching on the frame
                // origin rather than the message keeps this robust as the beacon is rebuilt.
                // Only ignored when NO frame names a non-third-party file, so a real app error
                // that merely surfaces through a beacon callback still reports.
                var thirdPartyHosts = [
                    'static.cloudflareinsights.com',
                    'www.googletagmanager.com',
                    'www.google-analytics.com',
                    'region1.google-analytics.com',
                    'pagead2.googlesyndication.com',
                    'connect.facebook.net',
                    // index.html loads instafeed.js from unpkg; it reads Instagram's Content-Type
                    // header without a null check and throws on an answer that has none (#2324)
                    'unpkg.com/instafeed.js'
                ];
                var namedFrames = 0;
                var thirdPartyFrames = 0;
                if (frames) {
                    for (var n = 0; n < frames.length; n++) {
                        var namedFile = frames[n].filename;
                        if (!namedFile || namedFile === '(unknown)') continue;
                        namedFrames++;
                        for (var h = 0; h < thirdPartyHosts.length; h++) {
                            if (namedFile.indexOf(thirdPartyHosts[h]) !== -1) {
                                thirdPartyFrames++;
                                break;
                            }
                        }
                    }
                }
                if (namedFrames > 0 && namedFrames === thirdPartyFrames) {
                    return true;
                }
                // Filter bare cross-origin "Script error." (Rollbar #18, ~29k occurrences — our
                // single largest item). The browser strips message, file and line for scripts
                // served from another origin, so these carry nothing to act on.
                // NB: these payloads are NOT frame-less — Rollbar synthesises a single frame with
                // filename "(unknown)" and a null lineno, so testing frames.length === 0 never
                // matches. Require the absence of any frame naming a real file instead, which
                // still lets a same-origin "Script error." through if one ever appears.
                var hasIdentifiedFrame = false;
                if (frames) {
                    for (var f = 0; f < frames.length; f++) {
                        var frameFile = frames[f].filename;
                        if (frameFile && frameFile !== '(unknown)') {
                            hasIdentifiedFrame = true;
                            break;
                        }
                    }
                }
                if (message && message.indexOf('Script error') !== -1 && !hasIdentifiedFrame) {
                    return true;
                }
                // Filter frameless errors whose whole message is a bare minified identifier
                // ("Ba", "ga", "Ca", "a"). A third-party SDK loaded from an inline snippet
                // throws these during silent login with no resolvable stack — every frame is
                // "(unknown)". The minified name drifts as that library is rebuilt (Ji/Mi/"Ba"
                // one week, Ii/Li/"Ca" the next), so each rebuild spawns a brand-new Rollbar
                // item: #2489, #2503, #2504, #2505, #2517, #2518. Our own bundle is
                // same-origin, so its errors always carry a named frame and a real message.
                // Newer builds of that SDK escape the frameless check by attributing one frame
                // to the bare page URL — at a line number far past the end of our ~120-line
                // index.html, i.e. an injected script, not our markup (Rollbar #2526, #2528).
                // So "named frame" here means "names a .js file": our own errors always point
                // at a bundle under /assets/, the SDK's never do.
                var hasNamedScriptFrame = false;
                if (frames) {
                    for (var s = 0; s < frames.length; s++) {
                        var scriptFile = frames[s].filename;
                        if (scriptFile && scriptFile !== '(unknown)' && /\.js([?#]|$)/.test(scriptFile)) {
                            hasNamedScriptFrame = true;
                            break;
                        }
                    }
                }
                if (message && !hasNamedScriptFrame && /^[A-Za-z$_][A-Za-z0-9$_]{0,2}$/.test(message)) {
                    return true;
                }
                // Chrome on iOS throws the same family through the same Pi/Si/Aw promise chain with
                // a template-literal tail on the minified name: "Ka`prod" (Rollbar #2571, #2575).
                if (message && !hasNamedScriptFrame && /^[A-Za-z$_][A-Za-z0-9$_]{0,2}`/.test(message)) {
                    return true;
                }
                // The same SDK's companion TypeError (Rollbar #2525, #2529): Safari's wording
                // for a member access on a minified identifier ("a.I"), through the same
                // Ji/Mi call chain, with no script file anywhere on the stack. Real bugs of
                // this class evaluate real expressions (#2089's 'e.packs[0].paymentHub') or
                // carry a bundle frame, so both keep reporting.
                if (message && !hasNamedScriptFrame
                    && /^undefined is not an object \(evaluating '[A-Za-z$_][A-Za-z0-9$_]{0,2}\.[A-Za-z$_][A-Za-z0-9$_]{0,2}'\)$/.test(message)) {
                    return true;
                }
                // The companion of the IDB wipe above (Rollbar #258, 10ms after #28 in the same
                // session): the deletion aborts Firebase's pending IDB request, which surfaces as a
                // frameless DOMException AbortError. Nothing in our own code creates an
                // AbortController, so a script-frameless abort with this exact wording is always
                // the SDK's; an abort that names our bundle keeps reporting.
                // Safari raises the same abort with an empty DOMException message, which Rollbar
                // fills in with the class name — message "AbortError", frames: [] (Rollbar #53,
                // each one right after an IDB "without an in-progress transaction" log).
                // Chrome words it "The connection was closed." when the wipe cuts the IDB request
                // off (Rollbar #83, 115ms after #2573's "Force close delete origin").
                var exceptionClass = trace && trace.exception && trace.exception.class;
                if (message && !hasNamedScriptFrame && exceptionClass === 'AbortError'
                    && (message.indexOf('The operation was aborted') !== -1 || message === 'AbortError'
                        || message.indexOf('The connection was closed') !== -1)) {
                    return true;
                }
                // Rollbar #21: an unhandled rejection whose `reason` Rollbar could not even read (a
                // promise from an injected script in another realm), with no script on the stack.
                if (message && !hasNamedScriptFrame && message.indexOf('error getting `reason` from event') !== -1) {
                    return true;
                }
                // Rollbar #125: Facebook's Android in-app browser evaluating a truncated injected
                // script on the home page — the only "frame" is the error text itself. A truncated
                // chunk of our own would name its /assets/ file, and keeps reporting.
                if (message && !hasNamedScriptFrame && exceptionClass === 'SyntaxError' && message === 'Unexpected end of input') {
                    return true;
                }
                // Rollbar #2514 / #2262: Firebase Analytics initialising while the browser reports
                // itself offline — the Installations SDK rejects its own registration promise.
                // Firebase is bundled, so the frames name our /assets/ chunk; the SDK's error code
                // is what identifies it, and nothing of ours produces it.
                if (message && message.indexOf('(installations/app-offline)') !== -1) {
                    return true;
                }
                // Rollbar #2572: getMessaging() re-runs Firebase's browser-support check without
                // awaiting it and throws from that detached promise, so our `await isSupported()`
                // cannot catch it. It fails when the origin is wiped between the two checks (same
                // session as #2573). Bundled like the above, so identified by its error code.
                if (message && message.indexOf('(messaging/unsupported-browser)') !== -1) {
                    return true;
                }
                // Rollbar #1577: DuckDuckGo on iOS calling its own native message handler after
                // the tab sat idle for hours and the handler went away, frames: [].
                if (message && !hasNamedScriptFrame && message === 'Script message handler is unavailable') {
                    return true;
                }
                // Rollbar #2066: a frameless "invalid origin" from a script on an iPhone left idle
                // on the shop. Neither the client, the web-shop proxy nor the game backend has that
                // wording; one that names our bundle keeps reporting.
                if (message && !hasNamedScriptFrame && message === 'invalid origin') {
                    return true;
                }
                // Rollbar #2588: Facebook's iOS in-app browser (FBAN/FBIOS) injects a script that
                // reads its own window._META.IABInnerFrame before the host defines it, attributed to
                // the page URL at line 1. The app never touches _META; one naming our bundle reports.
                if (message && !hasNamedScriptFrame && message.indexOf('window._META') !== -1) {
                    return true;
                }
                return false;
            },
            payload: {
                environment: "VITE_ENVIRONMENT",
                client: {
                    javascript: {
                        code_version: "__VERSION__",
                    }
                },
            }
        };
        // Rollbar Snippet
        !function (r) {
            var e = {};

            function o(n) {
                if (e[n]) return e[n].exports;
                var t = e[n] = {i: n, l: !1, exports: {}};
                return r[n].call(t.exports, t, t.exports, o), t.l = !0, t.exports
            }

            o.m = r, o.c = e, o.d = function (r, e, n) {
                o.o(r, e) || Object.defineProperty(r, e, {enumerable: !0, get: n})
            }, o.r = function (r) {
                "undefined" != typeof Symbol && Symbol.toStringTag && Object.defineProperty(r, Symbol.toStringTag, {value: "Module"}), Object.defineProperty(r, "__esModule", {value: !0})
            }, o.t = function (r, e) {
                if (1 & e && (r = o(r)), 8 & e) return r;
                if (4 & e && "object" == typeof r && r && r.__esModule) return r;
                var n = Object.create(null);
                if (o.r(n), Object.defineProperty(n, "default", {
                    enumerable: !0,
                    value: r
                }), 2 & e && "string" != typeof r) for (var t in r) o.d(n, t, function (e) {
                    return r[e]
                }.bind(null, t));
                return n
            }, o.n = function (r) {
                var e = r && r.__esModule ? function () {
                    return r.default
                } : function () {
                    return r
                };
                return o.d(e, "a", e), e
            }, o.o = function (r, e) {
                return Object.prototype.hasOwnProperty.call(r, e)
            }, o.p = "", o(o.s = 0)
        }([function (r, e, o) {
            "use strict";
            var n = o(1), t = o(5);
            _rollbarConfig = _rollbarConfig || {}, _rollbarConfig.rollbarJsUrl = _rollbarConfig.rollbarJsUrl || "https://cdn.rollbar.com/rollbarjs/refs/tags/v2.26.1/rollbar.min.js", _rollbarConfig.async = void 0 === _rollbarConfig.async || _rollbarConfig.async;
            var a = n.setupShim(window, _rollbarConfig), l = t(_rollbarConfig);
            window.rollbar = n.Rollbar, a.loadFull(window, document, !_rollbarConfig.async, _rollbarConfig, l)
        }, function (r, e, o) {
            "use strict";
            var n = o(2), t = o(3);

            function a(r) {
                return function () {
                    try {
                        return r.apply(this, arguments)
                    } catch (r) {
                        try {
                            console.error("[Rollbar]: Internal error", r)
                        } catch (r) {
                        }
                    }
                }
            }

            var l = 0;

            function i(r, e) {
                this.options = r, this._rollbarOldOnError = null;
                var o = l++;
                this.shimId = function () {
                    return o
                }, "undefined" != typeof window && window._rollbarShims && (window._rollbarShims[o] = {
                    handler: e,
                    messages: []
                })
            }

            var s = o(4), d = function (r, e) {
                return new i(r, e)
            }, c = function (r) {
                return new s(d, r)
            };

            function u(r) {
                return a((function () {
                    var e = this, o = Array.prototype.slice.call(arguments, 0),
                        n = {shim: e, method: r, args: o, ts: new Date};
                    window._rollbarShims[this.shimId()].messages.push(n)
                }))
            }

            i.prototype.loadFull = function (r, e, o, n, t) {
                var l = !1, i = e.createElement("script"), s = e.getElementsByTagName("script")[0], d = s.parentNode;
                i.crossOrigin = "", i.src = n.rollbarJsUrl, o || (i.async = !0), i.onload = i.onreadystatechange = a((function () {
                    if (!(l || this.readyState && "loaded" !== this.readyState && "complete" !== this.readyState)) {
                        i.onload = i.onreadystatechange = null;
                        try {
                            d.removeChild(i)
                        } catch (r) {
                        }
                        l = !0, function () {
                            var e;
                            if (void 0 === r._rollbarDidLoad) {
                                e = new Error("rollbar.js did not load");
                                for (var o, n, a, l, i = 0; o = r._rollbarShims[i++];) for (o = o.messages || []; n = o.shift();) for (a = n.args || [], i = 0; i < a.length; ++i) if ("function" == typeof (l = a[i])) {
                                    l(e);
                                    break
                                }
                            }
                            "function" == typeof t && t(e)
                        }()
                    }
                })), d.insertBefore(i, s)
            }, i.prototype.wrap = function (r, e, o) {
                try {
                    var n;
                    if (n = "function" == typeof e ? e : function () {
                        return e || {}
                    }, "function" != typeof r) return r;
                    if (r._isWrap) return r;
                    if (!r._rollbar_wrapped && (r._rollbar_wrapped = function () {
                        o && "function" == typeof o && o.apply(this, arguments);
                        try {
                            return r.apply(this, arguments)
                        } catch (o) {
                            var e = o;
                            throw e && ("string" == typeof e && (e = new String(e)), e._rollbarContext = n() || {}, e._rollbarContext._wrappedSource = r.toString(), window._rollbarWrappedError = e), e
                        }
                    }, r._rollbar_wrapped._isWrap = !0, r.hasOwnProperty)) for (var t in r) r.hasOwnProperty(t) && (r._rollbar_wrapped[t] = r[t]);
                    return r._rollbar_wrapped
                } catch (e) {
                    return r
                }
            };
            for (var p = "log,debug,info,warn,warning,error,critical,global,configure,handleUncaughtException,handleAnonymousErrors,handleUnhandledRejection,captureEvent,captureDomContentLoaded,captureLoad".split(","), f = 0; f < p.length; ++f) i.prototype[p[f]] = u(p[f]);
            r.exports = {
                setupShim: function (r, e) {
                    if (r) {
                        var o = e.globalAlias || "Rollbar";
                        if ("object" == typeof r[o]) return r[o];
                        r._rollbarShims = {}, r._rollbarWrappedError = null;
                        var l = new c(e);
                        return a((function () {
                            e.captureUncaught && (l._rollbarOldOnError = r.onerror, n.captureUncaughtExceptions(r, l, !0), e.wrapGlobalEventHandlers && t(r, l, !0)), e.captureUnhandledRejections && n.captureUnhandledRejections(r, l, !0);
                            var a = e.autoInstrument;
                            return !1 !== e.enabled && (void 0 === a || !0 === a || function (r) {
                                return !("object" != typeof r || void 0 !== r.page && !r.page)
                            }(a)) && r.addEventListener && (r.addEventListener("load", l.captureLoad.bind(l)), r.addEventListener("DOMContentLoaded", l.captureDomContentLoaded.bind(l))), r[o] = l, l
                        }))()
                    }
                }, Rollbar: c
            }
        }, function (r, e, o) {
            "use strict";

            function n(r, e, o, n) {
                r._rollbarWrappedError && (n[4] || (n[4] = r._rollbarWrappedError), n[5] || (n[5] = r._rollbarWrappedError._rollbarContext), r._rollbarWrappedError = null);
                var t = e.handleUncaughtException.apply(e, n);
                o && o.apply(r, n), "anonymous" === t && (e.anonymousErrorsPending += 1)
            }

            r.exports = {
                captureUncaughtExceptions: function (r, e, o) {
                    if (r) {
                        var t;
                        if ("function" == typeof e._rollbarOldOnError) t = e._rollbarOldOnError; else if (r.onerror) {
                            for (t = r.onerror; t._rollbarOldOnError;) t = t._rollbarOldOnError;
                            e._rollbarOldOnError = t
                        }
                        e.handleAnonymousErrors();
                        var a = function () {
                            var o = Array.prototype.slice.call(arguments, 0);
                            n(r, e, t, o)
                        };
                        o && (a._rollbarOldOnError = t), r.onerror = a
                    }
                }, captureUnhandledRejections: function (r, e, o) {
                    if (r) {
                        "function" == typeof r._rollbarURH && r._rollbarURH.belongsToShim && r.removeEventListener("unhandledrejection", r._rollbarURH);
                        var n = function (r) {
                            var o, n, t;
                            try {
                                o = r.reason
                            } catch (r) {
                                o = void 0
                            }
                            try {
                                n = r.promise
                            } catch (r) {
                                n = "[unhandledrejection] error getting `promise` from event"
                            }
                            try {
                                t = r.detail, !o && t && (o = t.reason, n = t.promise)
                            } catch (r) {
                            }
                            o || (o = "[unhandledrejection] error getting `reason` from event"), e && e.handleUnhandledRejection && e.handleUnhandledRejection(o, n)
                        };
                        n.belongsToShim = o, r._rollbarURH = n, r.addEventListener("unhandledrejection", n)
                    }
                }
            }
        }, function (r, e, o) {
            "use strict";

            function n(r, e, o) {
                if (e.hasOwnProperty && e.hasOwnProperty("addEventListener")) {
                    for (var n = e.addEventListener; n._rollbarOldAdd && n.belongsToShim;) n = n._rollbarOldAdd;
                    var t = function (e, o, t) {
                        n.call(this, e, r.wrap(o), t)
                    };
                    t._rollbarOldAdd = n, t.belongsToShim = o, e.addEventListener = t;
                    for (var a = e.removeEventListener; a._rollbarOldRemove && a.belongsToShim;) a = a._rollbarOldRemove;
                    var l = function (r, e, o) {
                        a.call(this, r, e && e._rollbar_wrapped || e, o)
                    };
                    l._rollbarOldRemove = a, l.belongsToShim = o, e.removeEventListener = l
                }
            }

            r.exports = function (r, e, o) {
                if (r) {
                    var t, a,
                        l = "EventTarget,Window,Node,ApplicationCache,AudioTrackList,ChannelMergerNode,CryptoOperation,EventSource,FileReader,HTMLUnknownElement,IDBDatabase,IDBRequest,IDBTransaction,KeyOperation,MediaController,MessagePort,ModalWindow,Notification,SVGElementInstance,Screen,TextTrack,TextTrackCue,TextTrackList,WebSocket,WebSocketWorker,Worker,XMLHttpRequest,XMLHttpRequestEventTarget,XMLHttpRequestUpload".split(",");
                    for (t = 0; t < l.length; ++t) r[a = l[t]] && r[a].prototype && n(e, r[a].prototype, o)
                }
            }
        }, function (r, e, o) {
            "use strict";

            function n(r, e) {
                this.impl = r(e, this), this.options = e, function (r) {
                    for (var e = function (r) {
                        return function () {
                            var e = Array.prototype.slice.call(arguments, 0);
                            if (this.impl[r]) return this.impl[r].apply(this.impl, e)
                        }
                    }, o = "log,debug,info,warn,warning,error,critical,global,configure,handleUncaughtException,handleAnonymousErrors,handleUnhandledRejection,_createItem,wrap,loadFull,shimId,captureEvent,captureDomContentLoaded,captureLoad".split(","), n = 0; n < o.length; n++) r[o[n]] = e(o[n])
                }(n.prototype)
            }

            n.prototype._swapAndProcessMessages = function (r, e) {
                var o, n, t;
                for (this.impl = r(this.options); o = e.shift();) n = o.method, t = o.args, this[n] && "function" == typeof this[n] && ("captureDomContentLoaded" === n || "captureLoad" === n ? this[n].apply(this, [t[0], o.ts]) : this[n].apply(this, t));
                return this
            }, r.exports = n
        }, function (r, e, o) {
            "use strict";
            r.exports = function (r) {
                return function (e) {
                    if (!e && !window._rollbarInitialized) {
                        for (var o, n, t = (r = r || {}).globalAlias || "Rollbar", a = window.rollbar, l = function (r) {
                            return new a(r)
                        }, i = 0; o = window._rollbarShims[i++];) n || (n = o.handler), o.handler._swapAndProcessMessages(l, o.messages);
                        window[t] = n, window._rollbarInitialized = !0
                    }
                }
            }
        }]);
        // End Rollbar Snippet
    </script>

    <link href="https://fonts.googleapis.com" rel="preconnect"/>
    <link crossorigin href="https://fonts.gstatic.com" rel="preconnect"/>
    <link
            href="https://fonts.googleapis.com/css2?family=Fira+Sans+Condensed:wght@400;500;600&family=Roboto+Slab:wght@100;400;700;900&family=Noto+Sans:wght@500;700&display=swap"
            rel="stylesheet"
    />

    <script>
        // Define dataLayer and the gtag function.
        window.dataLayer = window.dataLayer || [];

        function gtag() {
            dataLayer.push(arguments);
        }

        // Default ad_storage to 'denied' as a placeholder
        // Determine actual values based on your own requirements
        gtag('consent', 'default', {
            ad_storage: 'denied',
            analytics_storage: 'denied',
        });
    </script>

    <!-- Global site tag (gtag.js) - Google Analytics -->
    <script
            async
            src="https://www.googletagmanager.com/gtag/js?id=G-PQ99NRKBXW"></script>
    <script>
        window.dataLayer = window.dataLayer || [];

        function gtag() {
            dataLayer.push(arguments);
        }

        gtag('js', new Date());
        gtag('config', 'G-PQ99NRKBXW');
    </script>
</svelte:head>
<!-- Behind the i18n gate like the footer below (each routed page gates itself): before it lifts
     the banner showed raw keys, and with no locale committed yet it threw and App never mounted
     (Rollbar #2579). -->
{#if !$isLoading}
    <GdprBanner
            cookieName="gdpr"
            description={$_('WebshopCookiesDescription')}
            heading={$_('WebshopCookiesTitle')}
            onanalytics={initAnalytics}
            onmarketing={initMarketing}
            ontracking={initTracking}
    />
{/if}
{#if SnowfallComponent}
    <SnowfallComponent />
{/if}
<main class="{$user ? '' : window.location.pathname.replace('/','')}">
    <div>
        {#if matchPath('/', $currentPath) && $currentPath === '/'}
            <Home/>
        {:else if matchPath('/login', $currentPath)}
            <Login/>
        {:else if matchPath('/supplies/:section', $currentPath)}
            {#if $user}
                <Shop params={matchPath('/supplies/:section', $currentPath).params}/>
            {:else if !$user}
                <Login/>
            {/if}
        {:else if $currentPath === '/supplies'}
            {#if $user}
                <Shop params={{ section: 'featured' }}/>
            {:else}
                <Login/>
            {/if}
        {:else if matchPath('/buyPack', $currentPath)}
            {#if $user}
                <BuyPack/>
            {:else}
                <Login/>
            {/if}
        {:else if matchPath('/terms-of-service', $currentPath)}
            <Tos/>
        {:else if matchPath('/privacy-policy', $currentPath)}
            <Privacy/>
        {:else if matchPath('/delete-account', $currentPath)}
            <DeleteAccount/>
        {:else if isMigrationPagePath($currentPath)}
            <RfbMigration/>
        {:else}
            <Home/>
        {/if}
    </div>
    {#if !$isLoading}
        {#if adventVariant($adventCalendarEvent.uniqueIdentifier) === 'april' && $storeSection === 'adventCalendar'}
            <img alt="Easter Egg Hunt Glow" src="/images/adventCalendar/glow_april.png" class="aprilAdventGlow"/>
        {/if}
        {#if adventVariant($adventCalendarEvent.uniqueIdentifier) === 'sept' && $storeSection === 'adventCalendar'}
            <img alt="Maryland Glow" src="/images/adventCalendar/glow_sept.png" class="septAdventGlow"/>
        {/if}
        {#if adventVariant($adventCalendarEvent.uniqueIdentifier) === 'july' && $storeSection === 'adventCalendar'}
            <img alt="World Cup Glow" src="/images/adventCalendar/glow_july.png" class="julyAdventGlow"/>
        {/if}
        <footer>
            <a href="/terms-of-service">
                {$_('TermsOfService')}
            </a>
            <a href="/privacy-policy">
                {$_('PrivacyPolicy')}
            </a>
            {#if $user}
                <a
                        href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#115;&#117;&#112;&#112;&#111;&#114;&#116;&#64;&#119;&#97;&#114;&#45;&#97;&#110;&#100;&#45;&#112;&#101;&#97;&#99;&#101;&#46;&#99;&#111;&#109;?subject=Web Shop Support&body={encodeMailtoBody($user.user.username, $user.user.id)}"
                >
                    {$_('ContactPageTitle')}
                </a>
            {:else}
                <a
                        href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#115;&#117;&#112;&#112;&#111;&#114;&#116;&#64;&#119;&#97;&#114;&#45;&#97;&#110;&#100;&#45;&#112;&#101;&#97;&#99;&#101;&#46;&#99;&#111;&#109;?subject=Web Shop Support"
                >
                    {$_('ContactPageTitle')}
                </a>
            {/if}
            <a href="/delete-account">
                {$_('ContactDeleteAccountButton')}
            </a>
            <p>&copy; {currentYear} Erepublik Labs - __VERSION__ + __TIMESTAMP__</p>
            <ul class="social-links">
                <li><a href="https://discord.gg/Aj4XSWrYs9" target="_blank">
                    <Fa icon={faDiscord} fw primaryColor={socialIconColor} size="1.5x"/>
                </a></li>
                <li><a href="https://www.facebook.com/warandpeacegame/" target="_blank">
                    <Fa icon={faFacebook} fw primaryColor={socialIconColor} size="1.5x"/>
                </a></li>
                <li><a href="https://www.instagram.com/warandpeacecivilwar/" target="_blank">
                    <Fa icon={faInstagram} fw primaryColor={socialIconColor} size="1.5x"/>
                </a></li>
                <li><a href="https://www.tiktok.com/@warand.peace" target="_blank">
                    <Fa icon={faTiktok} fw primaryColor={socialIconColor} size="1.5x"/>
                </a></li>
                <li><a href="https://war-and-peace.fandom.com/wiki/War_and_Peace_Civil_War_Clash_Wiki" target="_blank">
                    <Fa icon={faBookOpenCover} fw primaryColor={socialIconColor} size="1.5x"/>
                </a></li>
            </ul>
            <img
                    src="data:image/png;base64,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"
                    alt="STILLFRONT GROUP"
            />
        </footer>
    {/if}
</main>

<style lang="scss">
  //@import 'https://unpkg.com/chota@latest';
  @use './styles/global.scss';

  // Theme-specific styles are loaded dynamically via themeConfig


  main {
    display: flex;
    flex-direction: column;
    position: relative;

    // Clip horizontal overflow on mobile (overhanging card badges) without
    // creating a scroll container — `clip` (unlike hidden) doesn't force
    // overflow-y to auto, so the scroll-driven VanishingHeader keeps working.
    @media screen and (max-width: 768px) {
      overflow-x: clip;
    }

    &.login, &.shop {
      height: 100vh;
      justify-content: space-around;
    }
  }

  footer {
    border-top: 1px dotted rgba(255, 255, 255, 0.2);
    margin-top: 30px;
    padding-top: 10px;
    margin-bottom: 20px;
    display: flex;
    font-size: 0.7em;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    opacity: 0.5;
    transition: opacity 0.2s ease-in-out;
    position: relative;
    align-content: flex-start;
    //position: absolute;
    //bottom: 0;
    //width: 100%;
    //height: 85px;

    &:hover {
      opacity: 1;
    }

    a {
      padding: 0 5px;
    }

    p {
      color: rgba(255, 255, 255, 0.5);
      width: 100%;
      text-align: center;
    }

    img {
      margin-top: 15px;
      margin-bottom: 15px;
    }

    .social-links {
      display: flex;
      list-style: none;
      flex-basis: 100%;
      justify-content: center;
      margin-top: 10px;
    }
  }

  .aprilAdventGlow {
    position: absolute;
    bottom: 145px;
    z-index: -1;
    width: 100%;
    max-height: 500px;
  }

  .septAdventGlow {
    position: absolute;
    bottom: 145px;
    z-index: -1;
    width: 100%;
  }

  .julyAdventGlow {
    position: absolute;
    bottom: 145px;
    z-index: -1;
    width: 100%;
  }
</style>
